// Ordenamiento reutilizable para tablas: encabezados con flechitas (↑/↓) que
// alternan ascendente/descendente. Detecta automáticamente fechas (dd/mm/aaaa
// o ISO), números (aunque traigan Q, $, comas) y texto (insensible a acentos).
import React, { useCallback, useMemo, useState } from "react";
import { ArrowUp, ArrowDown, ChevronsUpDown } from "lucide-react";
import { normalize } from "./search";

const EMPTY_GETTERS = {};

function parseVal(v) {
  if (v === null || v === undefined || v === "") return { t: "empty", v: null };
  if (typeof v === "number") return { t: "num", v };
  if (typeof v === "boolean") return { t: "num", v: v ? 1 : 0 };
  const s = String(v).trim();
  let m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (m) return { t: "num", v: new Date(+m[3], +m[2] - 1, +m[1]).getTime() };
  m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return { t: "num", v: new Date(s).getTime() };
  if (/^[Q$€\s]*-?[\d,]+(\.\d+)?\s*%?$/.test(s)) {
    const n = Number(s.replace(/[^0-9.\-]/g, ""));
    if (!isNaN(n)) return { t: "num", v: n };
  }
  return { t: "str", v: normalize(s) };
}

export function sortRows(rows, key, dir, getters = {}) {
  if (!key || !Array.isArray(rows)) return rows;
  const get = getters[key] || ((r) => r[key]);
  const sign = dir === "desc" ? -1 : 1;
  return [...rows].sort((a, b) => {
    const pa = parseVal(get(a)), pb = parseVal(get(b));
    if (pa.t === "empty" && pb.t === "empty") return 0;
    if (pa.t === "empty") return 1;   // vacíos siempre al final
    if (pb.t === "empty") return -1;
    let c;
    if (pa.t === "num" && pb.t === "num") c = pa.v - pb.v;
    else c = String(pa.v).localeCompare(String(pb.v), "es");
    return c * sign;
  });
}

// Hook: devuelve un encabezado <Th> ya enlazado y una función para ordenar filas.
export function useSortable(initialKey = "", initialDir = "asc", getters = EMPTY_GETTERS) {
  const [sort, setSort] = useState({ key: initialKey, dir: initialDir });
  const toggle = useCallback((key) => {
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  }, []);
  const apply = useCallback((rows) => sortRows(rows, sort.key, sort.dir, getters), [sort, getters]);

  const Th = useCallback(({ field, children, className = "", align = "left", pad = "px-4 py-3", ...props }) => {
    const active = field && sort.key === field;
    const Icon = !active ? ChevronsUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
    const justify = align === "right" ? "justify-end" : align === "center" ? "justify-center" : "justify-start";
    return (
      <th
        className={`${pad} font-bold ${field ? "cursor-pointer select-none hover:text-primary transition-colors" : ""} ${className}`}
        onClick={field ? () => toggle(field) : undefined}
        data-testid={field ? `sort-${field}` : undefined}
        {...props}
      >
        <span className={`flex items-center gap-1 ${justify}`}>
          {children}
          {field && <Icon className={`h-3.5 w-3.5 shrink-0 ${active ? "text-primary" : "opacity-30"}`} />}
        </span>
      </th>
    );
  }, [sort, toggle]);

  return { sort, toggle, sortRows: apply, Th };
}
